import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import ErrorBoundary from "@/components/ErrorBoundary";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Shield } from "lucide-react";

// ALL pages eagerly loaded — no lazy/dynamic imports to avoid Safari TDZ chunk ordering bugs
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import AppLogin from "./pages/app/AppLogin";
import AppSignup from "./pages/app/AppSignup";
import ResetPassword from "./pages/app/ResetPassword";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import OfficerDashboard from "./pages/app/OfficerDashboard";
import SiteOwnerDashboard from "./pages/app/SiteOwnerDashboard";
import BusinessOwnerDashboard from "./pages/app/BusinessOwnerDashboard";
import AdminDashboard from "./pages/app/AdminDashboard";

const queryClient = new QueryClient();

const PageLoader = () => (
  <div className="min-h-screen bg-[#0a0a0f] flex items-center justify-center font-['Outfit',sans-serif]">
    <div className="flex flex-col items-center gap-4">
      <div className="w-12 h-12 rounded-xl border-2 border-amber-500 flex items-center justify-center animate-pulse"
        style={{ boxShadow: "0 0 20px rgba(240,180,41,0.2)" }}>
        <Shield className="w-6 h-6 text-amber-500" />
      </div>
      <p className="text-sm text-gray-500">Loading...</p>
    </div>
  </div>
);

const App = () => (
  <ErrorBoundary>
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
              <Routes>
                <Route path="/" element={<Index />} />
                <Route path="/app" element={<AppLogin />} />
                <Route path="/app/signup" element={<AppSignup />} />
                <Route path="/app/demo" element={<AppSignup />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/app/officer" element={
                  <ProtectedRoute allowedRoles={["officer"]}>
                    <OfficerDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/app/site-owner" element={
                  <ProtectedRoute allowedRoles={["site_owner"]}>
                    <SiteOwnerDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/app/business-owner" element={
                  <ProtectedRoute allowedRoles={["business_owner"]}>
                    <BusinessOwnerDashboard />
                  </ProtectedRoute>
                } />
                <Route path="/app/admin" element={
                  <ProtectedRoute allowedRoles={["admin"]}>
                    <AdminDashboard />
                  </ProtectedRoute>
                } />
                <Route path="*" element={<NotFound />} />
              </Routes>
          </BrowserRouter>
        </TooltipProvider>
      </AuthProvider>
    </QueryClientProvider>
  </ErrorBoundary>
);

export default App;
